import { v, type Infer } from "convex/values";

/** Les durées entières de `from` à `to` secondes, comme les liste l'API. */
function range(from: number, to: number): number[] {
  return Array.from({ length: to - from + 1 }, (_, index) => from + index);
}

/**
 * Modèles de génération vidéo proposés dans l'onglet « Générer » du VideoNode.
 * Même contrat que `imageModelOptions` (cf. imageModels.ts) : la liste est la source
 * de vérité, le validateur en dérive, donc retirer un modèle d'ici le fait
 * automatiquement rejeter par la mutation. Le premier est le défaut.
 *
 * Recopié depuis `GET https://openrouter.ai/api/v1/videos/models` le
 * 2026-11-03, en gardant la dernière génération de chaque famille (Veo 3.1,
 * Seedance 2.5, Wan 3.0, Kling 3.0, Hailuo 3, Grok Imagine 1.5, Flux.3) plus
 * quelques variantes rapides ou bon marché : `durations`, `resolutions`, `aspectRatios`, `frames` et
 * `audio` sont ceux annoncés par l'API (`supported_durations`,
 * `supported_resolutions`, `supported_aspect_ratios`, `supported_frame_images`,
 * `generate_audio`). Une valeur hors liste fait répondre 400 à OpenRouter :
 * la mutation les revalide donc avant de partir.
 *
 * Deux sortes d'images d'entrée, que l'API distingue :
 * - `frames` (`frame_images`) : la première et/ou la dernière image EXACTE de
 *   la vidéo (image-to-video). C'est le mode le plus prévisible.
 * - `maxReferenceImages` (`input_references`) : des images qui guident le style
 *   ou le contenu sans être des frames (reference-to-video).
 *
 * ⚠️ `maxReferenceImages` n'est PAS exposé par le catalogue OpenRouter (la
 * page de chaque modèle liste `input_references` avec un texte générique, ce
 * n'est pas une preuve). Vérifié le 2026-11-03 : Seedance 2.0 (9, exemple de
 * la doc OpenRouter) et 2.5 (30 images, annoncé par OpenRouter) sont
 * confirmés. Wan 3.0 (10) et Hailuo 3 (9) l'annoncent chez le fournisseur
 * sans confirmation OpenRouter : gardés plus bas. Grok 1.5 (14), Veo 3.1 (3,
 * clips de 8 s) et Flux.3 (10) le font chez le fournisseur mais rien ne dit
 * qu'OpenRouter le transmet : 0. Les plafonds
 * ci-dessous sont volontairement prudents (un plafond trop généreux fait
 * échouer la requête chez le fournisseur) ; `0` pour tout modèle dont la
 * description n'annonce pas ce mode.
 *
 * `pricePerSecond` sert UNIQUEMENT à l'affichage (estimation : le prix réel
 * dépend de la résolution et de l'audio, et certains modèles facturent au
 * token). Le coût compté vient du `usage.cost` du job, jamais d'un calcul
 * local.
 */
export const videoModelOptions = [
  {
    label: "Veo 3.1 Lite",
    value: "google/veo-3.1-lite",
    pricePerSecond: "0.05",
    durations: [4, 6, 8],
    defaultDuration: 4,
    resolutions: ["720p", "1080p"],
    aspectRatios: ["16:9", "9:16"],
    frames: ["first_frame", "last_frame"],
    maxReferenceImages: 0,
    audio: "toggle",
  },
  {
    // The Biological Computing Co. Le moins cher du catalogue, mais 16:9
    // seul, 3 ou 5 s, pas de son, frame de début seulement.
    label: "Mischief 1",
    value: "tbc/mischief-1",
    pricePerSecond: "0.01",
    durations: [3, 5],
    defaultDuration: 5,
    resolutions: ["720p", "480p"],
    aspectRatios: ["16:9"],
    frames: ["first_frame"],
    maxReferenceImages: 0,
    audio: "none",
  },
  {
    label: "Wan 3.0",
    value: "alibaba/wan-3.0",
    // 720p ; 0.05 $/s en 480p, 0.20 $/s en 1080p.
    pricePerSecond: "0.10",
    durations: range(2, 30),
    defaultDuration: 5,
    resolutions: ["720p", "480p", "1080p"],
    aspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4"],
    frames: ["first_frame", "last_frame"],
    maxReferenceImages: 4,
    audio: "toggle",
  },
  {
    label: "Seedance 2.0 Fast",
    value: "bytedance/seedance-2.0-fast",
    // Facturé au token vidéo : ~0.09 $/s en 720p, ~0.04 $/s en 480p.
    pricePerSecond: "0.09",
    durations: range(4, 15),
    defaultDuration: 5,
    resolutions: ["720p", "480p"],
    aspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9", "9:21"],
    frames: ["first_frame", "last_frame"],
    maxReferenceImages: 9,
    audio: "toggle",
  },
  {
    label: "Veo 3.1 Fast",
    value: "google/veo-3.1-fast",
    pricePerSecond: "0.10",
    durations: [4, 6, 8],
    defaultDuration: 4,
    resolutions: ["720p", "1080p", "4K"],
    aspectRatios: ["16:9", "9:16"],
    frames: ["first_frame", "last_frame"],
    maxReferenceImages: 0,
    audio: "toggle",
  },
  {
    label: "Kling 3.0 Pro",
    value: "kwaivgi/kling-v3.0-pro",
    // Avec le son (défaut) ; 0.11 $/s sans.
    pricePerSecond: "0.17",
    durations: range(3, 15),
    defaultDuration: 5,
    resolutions: ["720p"],
    aspectRatios: ["16:9", "9:16", "1:1"],
    frames: ["first_frame", "last_frame"],
    maxReferenceImages: 0,
    audio: "toggle",
  },
  {
    label: "Hailuo 3",
    value: "minimax/hailuo-3",
    // + 0.04 $ par image de référence.
    pricePerSecond: "0.13",
    durations: range(5, 15),
    defaultDuration: 5,
    resolutions: ["2K"],
    aspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"],
    frames: ["first_frame", "last_frame"],
    maxReferenceImages: 4,
    audio: "toggle",
  },
  {
    label: "Grok Imagine Video 1.5",
    value: "x-ai/grok-imagine-video-1.5",
    // 720p ; 0.08 $/s en 480p, 0.25 $/s en 1080p.
    pricePerSecond: "0.14",
    durations: range(1, 15),
    defaultDuration: 5,
    resolutions: ["720p", "480p", "1080p"],
    aspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4", "3:2", "2:3"],
    frames: ["first_frame"],
    maxReferenceImages: 0,
    audio: "always",
  },
  {
    label: "Flux.3 Video",
    value: "black-forest-labs/flux-3-video",
    // 720p ; 0.29 $/s en 1080p, jusqu'à 0.41 $/s en 4K.
    pricePerSecond: "0.17",
    durations: range(5, 20),
    defaultDuration: 5,
    resolutions: ["720p", "1080p", "2K", "4K"],
    aspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"],
    frames: ["first_frame", "last_frame"],
    maxReferenceImages: 0,
    audio: "toggle",
  },
  {
    label: "Seedance 2.5",
    value: "bytedance/seedance-2.5",
    // Facturé au token vidéo : ~0.23 $/s en 720p, ~0.10 $/s en 480p.
    pricePerSecond: "0.23",
    durations: range(4, 30),
    defaultDuration: 5,
    resolutions: ["720p", "480p"],
    aspectRatios: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"],
    frames: ["first_frame", "last_frame"],
    maxReferenceImages: 30,
    audio: "toggle",
  },
  {
    label: "Veo 3.1",
    value: "google/veo-3.1",
    pricePerSecond: "0.40",
    durations: [4, 6, 8],
    defaultDuration: 4,
    resolutions: ["720p", "1080p", "4K"],
    aspectRatios: ["16:9", "9:16"],
    frames: ["first_frame", "last_frame"],
    maxReferenceImages: 0,
    audio: "toggle",
  },
] as const satisfies ReadonlyArray<{
  label: string;
  value: string;
  pricePerSecond: string;
  durations: readonly number[];
  defaultDuration: number;
  resolutions: readonly string[];
  aspectRatios: readonly string[];
  frames: readonly ("first_frame" | "last_frame")[];
  maxReferenceImages: number;
  /** `toggle` : `generate_audio` réglable ; `always` : le fournisseur sort
   *  toujours une piste son, sans option pour la couper (Grok) ; `none`. */
  audio: "toggle" | "always" | "none";
}>;

export const videoModelValues = videoModelOptions.map((model) => model.value);

export const vVideoModelValues = v.union(
  ...videoModelValues.map((model) => v.literal(model)),
);

export type VideoModelValues = typeof vVideoModelValues.type;

export type VideoModelOption = (typeof videoModelOptions)[number];

/**
 * Le rôle de chaque image branchée en entrée :
 * - `start` / `end` : l'image EXACTE qui ouvre / ferme la vidéo
 *   (`frame_images`, image-to-video) ;
 * - `reference` : guide le style ou le contenu sans être une frame
 *   (`input_references`) ;
 * - `ignore` : branchée, mais pas envoyée.
 *
 * Frames et références ne se combinent pas : quand les deux sont envoyés,
 * OpenRouter traite la requête en image-to-video et ignore les références en
 * silence. Le mélange est donc refusé, jamais envoyé.
 */
export const videoImageRoles = ["start", "end", "reference", "ignore"] as const;

export type VideoImageRole = (typeof videoImageRoles)[number];

export const vVideoImageRole = v.union(
  ...videoImageRoles.map((role) => v.literal(role)),
);

/** Un rôle choisi par l'utilisateur, attaché à l'URL de l'image (stable quand
 *  on réordonne les edges, contrairement à une position). */
export const vVideoImageRoleEntry = v.object({
  url: v.string(),
  role: vVideoImageRole,
});

export type VideoImageRoleEntry = Infer<typeof vVideoImageRoleEntry>;

/** Les rôles stockés (`values.videoImageRoles`), ce qui est lisible. */
export function readVideoImageRoles(value: unknown): VideoImageRoleEntry[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is VideoImageRoleEntry =>
      !!entry &&
      typeof entry === "object" &&
      typeof (entry as VideoImageRoleEntry).url === "string" &&
      videoImageRoles.includes((entry as VideoImageRoleEntry).role),
  );
}

/**
 * Le rôle de chaque image d'entrée, dans l'ordre d'envoi : celui que
 * l'utilisateur a choisi s'il y en a un, sinon un défaut.
 *
 * Tant que rien n'est choisi, le défaut remplit la frame de début, puis celle
 * de fin (si le modèle en a une), et ignore le reste. Dès qu'un rôle est
 * choisi pour une image branchée, une image arrivée ensuite (collaborateur,
 * Nolë, upload qui se termine) est ignorée : sinon elle deviendrait en douce
 * la frame de début d'une vidéo que l'utilisateur a réglée en « texte seul »
 * ou en références. Jamais de défaut `reference` : il mélangerait frames et
 * références.
 *
 * Un rôle choisi que le modèle ne sait pas faire (frame de fin chez un modèle
 * qui n'en a pas…) est gardé tel quel : c'est `checkVideoImageRoles` qui le
 * signale, plutôt que de changer le choix de l'utilisateur dans son dos.
 */
export function resolveVideoImageRoles(
  urls: string[],
  chosen: VideoImageRoleEntry[],
  model: VideoModelOption,
): VideoImageRole[] {
  const roles: Array<VideoImageRole | undefined> = urls.map(
    (url) => chosen.find((entry) => entry.url === url)?.role,
  );
  const frames: readonly string[] = model.frames;
  const hasChosen = roles.some((role) => role !== undefined);
  for (let index = 0; index < roles.length; index++) {
    if (roles[index]) continue;
    if (hasChosen) {
      roles[index] = "ignore";
    } else if (frames.includes("first_frame") && !roles.includes("start")) {
      roles[index] = "start";
    } else if (frames.includes("last_frame") && !roles.includes("end")) {
      roles[index] = "end";
    } else {
      roles[index] = "ignore";
    }
  }
  return roles as VideoImageRole[];
}

/** Le nom lisible d'un modèle vidéo, ou son slug s'il a quitté le catalogue. */
export function videoModelLabel(model: string): string {
  return videoModelOptions.find((option) => option.value === model)?.label ?? model;
}

export function findVideoModel(
  model: VideoModelValues,
): VideoModelOption | undefined {
  return videoModelOptions.find((option) => option.value === model);
}

export const vVideoFrameType = v.union(
  v.literal("first_frame"),
  v.literal("last_frame"),
);

export const vVideoFrameImage = v.object({
  url: v.string(),
  frameType: vVideoFrameType,
});

/**
 * Le poller abandonne au-delà : une vidéo de quelques secondes sort en 1 à
 * 5 minutes chez tous les fournisseurs du catalogue, 30 minutes veut dire
 * qu'elle ne sortira plus.
 */
export const VIDEO_GENERATION_DEADLINE_MS = 30 * 60 * 1000;

/**
 * Au-delà, un statut `running` ne peut plus correspondre à un poller vivant
 * (il abandonne lui-même à la deadline) : le serveur accepte une relance et
 * l'UI cesse d'afficher la génération. Sans cette sortie de secours, un
 * poller tué en plein vol verrouillerait le node.
 */
export const STALE_VIDEO_GENERATION_MS =
  VIDEO_GENERATION_DEADLINE_MS + 5 * 60 * 1000;

export type VideoInputImagesProblem =
  | { kind: "startNotSupported" }
  | { kind: "endNotSupported" }
  | { kind: "duplicateFrame" }
  | { kind: "mixedFramesAndReferences" }
  | { kind: "noReferencesSupport" }
  | { kind: "tooManyReferences"; images: number; max: number };

/**
 * Ce que le modèle ne peut pas prendre parmi les rôles d'images, ou `null`.
 * Partagé : le serveur en fait une erreur (cf. `generateVideo`), le client
 * grise le bouton et l'explique. Rien n'est jamais retiré en silence.
 */
export function checkVideoImageRoles(
  roles: VideoImageRole[],
  model: VideoModelOption,
): VideoInputImagesProblem | null {
  const count = (role: VideoImageRole) =>
    roles.filter((candidate) => candidate === role).length;
  const frames: readonly string[] = model.frames;
  const references = count("reference");

  if (count("start") > 1 || count("end") > 1) return { kind: "duplicateFrame" };
  if (count("start") > 0 && !frames.includes("first_frame")) {
    return { kind: "startNotSupported" };
  }
  if (count("end") > 0 && !frames.includes("last_frame")) {
    return { kind: "endNotSupported" };
  }
  if (references > 0 && count("start") + count("end") > 0) {
    return { kind: "mixedFramesAndReferences" };
  }
  if (references > 0 && model.maxReferenceImages === 0) {
    return { kind: "noReferencesSupport" };
  }
  if (references > model.maxReferenceImages) {
    return {
      kind: "tooManyReferences",
      images: references,
      max: model.maxReferenceImages,
    };
  }
  return null;
}

/**
 * Ce que le modèle reçoit pour chaque image, dans le vocabulaire du prompt
 * envoyé : la réécriture serveur des mentions, et ce que la pill affiche au
 * survol. `undefined` = l'image ne part pas.
 */
export function videoImageSentAs(
  roles: VideoImageRole[],
  index: number,
):
  | { kind: "start" }
  | { kind: "end" }
  | { kind: "reference"; position: number }
  | undefined {
  const role = roles[index];
  if (role === "start") return { kind: "start" };
  if (role === "end") return { kind: "end" };
  if (role === "reference") {
    return {
      kind: "reference",
      position: roles.slice(0, index).filter((r) => r === "reference").length + 1,
    };
  }
  return undefined;
}

/**
 * Les réglages d'une génération : ce que reçoit la mutation, ce qu'elle
 * transmet au job, et ce qu'elle stocke sur le node (`videoSettings`). Un
 * seul validateur pour les trois.
 */
export const vVideoGenerationSettings = v.object({
  model: vVideoModelValues,
  duration: v.number(),
  resolution: v.optional(v.string()),
  // Omis = le fournisseur déduit le format (de la frame de début quand il y
  // en a une, sinon son défaut).
  aspectRatio: v.optional(v.string()),
  generateAudio: v.optional(v.boolean()),
});

export type VideoGenerationSettings = Infer<typeof vVideoGenerationSettings>;

export const defaultVideoSettings: VideoGenerationSettings = {
  model: videoModelOptions[0].value,
  duration: videoModelOptions[0].defaultDuration,
};

/**
 * Les derniers réglages appliqués sur le node (ou par l'utilisateur, côté
 * `localStorage`), ramenés à ce que le modèle accepte : un réglage qu'il ne
 * connaît plus retombe sur son défaut au lieu de faire échouer l'envoi.
 */
export function readVideoSettings(
  value: unknown,
): VideoGenerationSettings | undefined {
  const settings = value as Partial<VideoGenerationSettings> | null | undefined;
  const model = videoModelOptions.find((o) => o.value === settings?.model);
  if (!model || !settings) return undefined;
  const durations: readonly number[] = model.durations;
  const resolutions: readonly string[] = model.resolutions;
  const aspectRatios: readonly string[] = model.aspectRatios;
  return {
    model: model.value,
    duration:
      typeof settings.duration === "number" &&
      durations.includes(settings.duration)
        ? settings.duration
        : model.defaultDuration,
    resolution:
      settings.resolution && resolutions.includes(settings.resolution)
        ? settings.resolution
        : undefined,
    aspectRatio:
      settings.aspectRatio && aspectRatios.includes(settings.aspectRatio)
        ? settings.aspectRatio
        : undefined,
    generateAudio:
      typeof settings.generateAudio === "boolean"
        ? settings.generateAudio
        : undefined,
  };
}

/**
 * Pourquoi une génération a échoué, en catégories que l'UI traduit. Le
 * message brut du fournisseur (souvent du JSON en anglais) reste à côté, en
 * détail : utile pour comprendre, illisible comme message principal.
 */
export const videoGenerationErrorCodes = [
  "timeout",
  "rejected",
  "tooLarge",
  "unknown",
] as const;

export type VideoGenerationErrorCode = (typeof videoGenerationErrorCodes)[number];

export const vVideoGenerationErrorCode = v.union(
  ...videoGenerationErrorCodes.map((code) => v.literal(code)),
);
